import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { Company } from '../../shared/models/core/company';
import { IPagedResult } from '../../shared/models/paged-result';
import { serialize } from '../../utility/serialize';
import { CompanyFilterSet } from '../../shared/models/core/company-filter-set';
import { DecryptedFieldDto } from '../../shared/dtos/core/decrypted-field.dto';
import { SensitiveCompanyField } from '../../shared/dtos/core/sensitive-company-field';

export const companiesApi = createApi({
  reducerPath: 'companiesApi',
  baseQuery,
  tagTypes: ['Companies'],
  endpoints: (builder) => ({
    getCompanies: builder.query<IPagedResult<Company>, CompanyFilterSet>({
      query: (filterSet) => ({
        url: `/api/companies?${serialize(filterSet)}`,
        method: 'GET',
      }),
      providesTags: ['Companies'],
    }),
    getCompany: builder.query<Company, string>({
      query: (id) => ({
        url: `/api/companies/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Companies', id }],
    }),
    addCompany: builder.mutation<Company, Partial<Company>>({
      query: (values) => ({
        url: `/api/companies`,
        method: 'POST',
        body: values,
      }),
      invalidatesTags: [{ type: 'Companies', id: 'LIST' }],
    }),
    updateCompany: builder.mutation<Company, Partial<Company>>({
      query: (values) => ({
        url: `/api/companies/${values.taxId}`,
        method: 'PUT',
        body: values,
      }),
      invalidatesTags: (_result, _error, { taxId }) => [{ type: 'Companies', id: taxId }],
    }),
    deleteCompany: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/companies/${id}`,
        method: 'DELETE',
      }),
    }),
    uploadCertificate: builder.mutation<{ tempKey: string }, FormData>({
      query: (values) => ({
        url: `/api/uploader`,
        method: 'POST',
        body: values,
      }),
    }),
    getCompanyFieldDecrypt: builder.query<DecryptedFieldDto, SensitiveCompanyField>({
      query: (model) => ({
        url: `/api/companies/${model.companyTaxId}/decrypt?field=${model.fieldName}`,
        method: 'GET',
      }),
      providesTags: ['Companies'],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useLazyGetCompaniesQuery,
  useAddCompanyMutation,
  useGetCompanyQuery,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useUploadCertificateMutation,
  useGetCompanyFieldDecryptQuery,
  useLazyGetCompanyFieldDecryptQuery,
} = companiesApi;
